import { useFrame, useThree } from '@react-three/fiber';
import { useExperienceStore } from '../state/useExperienceStore';
import * as THREE from 'three';
import { useRef, useEffect } from 'react';
import gsap from 'gsap';
import { easing } from 'maath';

export const CameraController = () => {
  const mode = useExperienceStore((state) => state.mode);
  const selectedShopId = useExperienceStore((state) => state.selectedShopId);
  const selectedProductId = useExperienceStore((state) => state.selectedProductId);
  const gl = useThree((state) => state.gl);
  
  const targetPos = useRef(new THREE.Vector3(0, 5, 20));
  const targetLookAt = useRef(new THREE.Vector3(0, 0, 0));

  // Manual control refs
  const moveSpeed = 15;
  const rotateSpeed = 0.002;
  const keys = useRef<Record<string, boolean>>({});
  const isMouseDown = useRef(false);
  const mouseRotation = useRef({ yaw: 0, pitch: 0 });
  const manualOffsetVec = useRef(new THREE.Vector3(0, 0, 0));

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => (keys.current[e.code] = true);
    const handleKeyUp = (e: KeyboardEvent) => (keys.current[e.code] = false);
    const handleMouseDown = () => (isMouseDown.current = true);
    const handleMouseUp = () => (isMouseDown.current = false);
    const handleMouseMove = (e: MouseEvent) => {
      if (isMouseDown.current && mode === 'HUB') {
        mouseRotation.current.yaw -= e.movementX * rotateSpeed;
        mouseRotation.current.pitch -= e.movementY * rotateSpeed;
        mouseRotation.current.pitch = Math.max(-Math.PI / 3, Math.min(Math.PI / 3, mouseRotation.current.pitch));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    gl.domElement.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      gl.domElement.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [gl, mode]);

  useEffect(() => {
    // Reset manual controls on mode change except HUB
    if (mode !== 'HUB') {
      mouseRotation.current = { yaw: 0, pitch: 0 };
      manualOffsetVec.current.set(0, 0, 0);
    }

    // Determine target based on mode
    let newPos = { x: 0, y: 5, z: 20 };
    let newLook = { x: 0, y: 0, z: 0 };

    if (mode === 'INTRO') {
      targetPos.current.set(0, 1500, 3000);
      targetLookAt.current.set(0, -200, -600);
    } else if (mode === 'PLANET') {
      newPos = { x: 45, y: 15, z: 140 };
      newLook = { x: 0, y: 0, z: -30 };
    } else if (mode === 'CONTINENT') {
      newPos = { x: 0, y: -40, z: 100 };
      newLook = { x: 0, y: -100, z: -200 };
    } else if (mode === 'COUNTRY') {
      newPos = { x: 0, y: -20, z: 60 };
      newLook = { x: 0, y: -80, z: -150 };
    } else if (mode === 'HUB') {
      newPos = { x: 0, y: 10, z: 50 };
      newLook = { x: 0, y: 5, z: 0 };
    } else if (mode === 'STORE') {
      newPos = { x: 0, y: 0, z: 25 };
      newLook = { x: 0, y: -5, z: -10 };
    } else if (mode === 'PRODUCT') {
      newPos = { x: 0, y: 0, z: 15 };
      newLook = { x: 0, y: 0, z: 0 };
    } else if (mode === 'GYM') {
      newPos = { x: 0, y: 5, z: 35 };
      newLook = { x: 0, y: 5, z: 0 };
    }

    gsap.to(targetPos.current, {
      x: newPos.x,
      y: newPos.y,
      z: newPos.z,
      duration: 1.2,
      ease: 'power4.out',
    });

    gsap.to(targetLookAt.current, {
      x: newLook.x,
      y: newLook.y,
      z: newLook.z,
      duration: 1.5,
      ease: 'power4.out',
    });
  }, [mode, selectedShopId, selectedProductId]);

  useFrame((state, delta) => {
    const { camera, mouse } = state;

    if (mode === 'HUB') {
      // Process Keyboard Movement
      const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion);
      const right = new THREE.Vector3(1, 0, 0).applyQuaternion(camera.quaternion);
      forward.y = 0; // Keep movement on horizontal plane
      right.y = 0;
      forward.normalize();
      right.normalize();

      const moveDir = new THREE.Vector3(0, 0, 0);
      if (keys.current['KeyW'] || keys.current['ArrowUp']) moveDir.add(forward);
      if (keys.current['KeyS'] || keys.current['ArrowDown']) moveDir.sub(forward);
      if (keys.current['KeyA'] || keys.current['ArrowLeft']) moveDir.sub(right);
      if (keys.current['KeyD'] || keys.current['ArrowRight']) moveDir.add(right);

      if (moveDir.length() > 0) {
        moveDir.normalize().multiplyScalar(moveSpeed * delta);
        manualOffsetVec.current.add(moveDir);

        // Apply Boundaries
        manualOffsetVec.current.x = Math.max(-100, Math.min(100, manualOffsetVec.current.x));
        manualOffsetVec.current.y = Math.max(-50, Math.min(100, manualOffsetVec.current.y));
        manualOffsetVec.current.z = Math.max(-200, Math.min(150, manualOffsetVec.current.z));
      }

      // Smooth dampen to combined position
      const finalTargetPos = targetPos.current.clone().add(manualOffsetVec.current);
      easing.damp3(camera.position, finalTargetPos, 0.2, delta);

      // Smooth LookAt with manual rotation
      const rotationQuat = new THREE.Quaternion().setFromEuler(new THREE.Euler(mouseRotation.current.pitch, mouseRotation.current.yaw, 0, 'YXZ'));
      const lookAtDir = new THREE.Vector3(0, 0, -1).applyQuaternion(rotationQuat);
      const finalLookAt = camera.position.clone().add(lookAtDir);
      
      camera.lookAt(finalLookAt);
    } else {
      // Standard smooth auto-navigation for other modes
      easing.damp3(camera.position, targetPos.current, 0.3, delta);
      
      const currentLookAt = camera.getWorldDirection(new THREE.Vector3()).add(camera.position);
      const lerpedLookAt = currentLookAt.lerp(targetLookAt.current, 0.1);
      
      const px = mouse.x * 2;
      const py = mouse.y * 1;
      camera.lookAt(lerpedLookAt.x + px, lerpedLookAt.y + py, lerpedLookAt.z);
    }

    // Update FOV dynamically
    let targetFov = 45;
    if (mode === 'HUB') targetFov = 50;
    else if (mode === 'STORE') targetFov = 35;
    else if (mode === 'PRODUCT') targetFov = 30;
    else if (mode === 'PLANET') targetFov = 60;
    else if (mode === 'CONTINENT') targetFov = 45;

    easing.damp(camera, 'fov', targetFov, 0.4, delta);
    camera.updateProjectionMatrix();
  });

  return null;
};
